import { CopilotStudioClient, ConnectionSettings } from "@microsoft/agents-copilotstudio-client";
import { useAuthStore, useCurrentUser } from "../../store/authStore";
import { useOutlookStore } from "../../store/outlookStore";
import { useAgentStore } from "../../store/agentStore";

declare global {
  interface Window {
    CONFIG?: {
      environmentId?: string;
      agentIdentifier?: string;
      appClientId?: string;
      clientId?: string;
      tenantId?: string;
      AI_API_KEY?: string;
      API_ENDPOINT?: string;
    };
  }
}

// 1. Connection settings for your Copilot Studio agent
const settings: ConnectionSettings = {
  environmentId: window.CONFIG?.environmentId || "Default-1be5b0cb-0e02-4548-be48-57b7a5c71228",
  agentIdentifier: window.CONFIG?.agentIdentifier || "crf6f_resourceFinderAssistant",
  appClientId: window.CONFIG?.clientId || "b7ec9375-2552-4bbc-8505-351e03b49ff1",
  tenantId: window.CONFIG?.tenantId || "1be5b0cb-0e02-4548-be48-57b7a5c71228",
};

let conversationId: string | null = null;

/**
 * Get current meeting context from Outlook Store
 */
const getCurrentMeetingContext = () => {
  const outlookState = useOutlookStore.getState();
  return {
    timeZone: outlookState.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    organizer: outlookState.organizer || "",
    subject: outlookState.subject || "",
    location: outlookState.location?.map((loc) => loc.displayName).join(", ") || "",
    startTime: outlookState.startTime,
    endTime: outlookState.endTime,
    requiredAttendees: outlookState.requiredAttendees || [],
    optionalAttendees: outlookState.optionalAttendees || [],
  };
};

/**
 * Format metadata from Outlook Store for Agent context
 */
const formatAgentMetadata = (
  userEmail?: string,
  startTimeString?: string,
  endTimeString?: string,
  timeZone?: string,
  organizer?: string
) => {
  return {
    Email: userEmail ?? "",
    StartTime: startTimeString ?? "",
    EndTime: endTimeString ?? "",
    TimeZone: timeZone ?? "UTC",
  };
};

/**
 * Get a fresh bearer token from the auth store (MSAL adapter takes care of refresh).
 */
const POWER_PLATFORM_SCOPE = "https://api.powerplatform.com/.default";

const fetchToken = async (): Promise<string> => {
  const store = useAuthStore.getState();
  const tokens = store.auth.tokens;

  if (tokens && tokens.scopes?.includes(POWER_PLATFORM_SCOPE) && store.isTokenValid() && !store.isTokenExpiringSoon()) {
    return tokens.accessToken;
  }

  return await store.refreshTokens([POWER_PLATFORM_SCOPE]);
};

/**
 * Create a new CopilotStudioClient instance with the latest token.
 */
// Cache Copilot client instance to avoid recreating it for every request
let clientInstance: CopilotStudioClient | null = null;
let clientToken: string | null = null;

/**
 * Create (or reuse) a CopilotStudioClient instance with the latest valid token.
 * Re-uses the same client as long as the underlying token is still valid, which
 * avoids the overhead of repeatedly creating new WebSocket / HTTP connections
 * on every single message.
 */
const createClient = async (): Promise<CopilotStudioClient> => {
  const token = await fetchToken();

  // If we already have a client and the token hasn't changed, reuse it
  if (clientInstance && clientToken === token) {
    return clientInstance;
  }

  // Otherwise create a new client (e.g. first time or after token refresh)
  clientToken = token;
  clientInstance = new CopilotStudioClient(settings, token);
  return clientInstance;
};

export const startNewChat = async () => {
  const client = await createClient();
  // Start a new conversation
  const replies = await client.startConversationAsync(true);
  // Extract conversation ID from the first reply if available
  conversationId = replies?.conversation?.id ?? null;
  return conversationId;
};

// Explicitly reset current conversation id
export const resetConversation = () => {
  conversationId = null;
};

/**
 * Export function to get current meeting context for external use
 */
export const getMeetingContext = getCurrentMeetingContext;

export const sendAgentsMessage = async (
  message: string,
  userEmail?: string,
  startTime?: Date | null,
  endTime?: Date | null,
  options?: {
    pagingAction?: "next" | "previous" | "goto";
    page?: number;
  }
): Promise<any> => {
  const client = await createClient();

  // Lấy meeting context từ outlookStore
  const meetingContext = getCurrentMeetingContext();

  // Sử dụng startTime/endTime từ parameters hoặc store
  const meetingStartTime = startTime || meetingContext.startTime;
  const meetingEndTime = endTime || meetingContext.endTime;

  // Fallback organizer nếu không có trong store
  const organizer = meetingContext.organizer || userEmail || "";

  // console.log(conversationId);
  // Nếu chưa có conversationId => khởi tạo cuộc trò chuyện mới trước khi gửi tin nhắn
  if (!conversationId) {
    await startNewChat();
  }

  const startTimeString = meetingStartTime ? meetingStartTime.toLocaleString() : new Date().toISOString();
  const endTimeString = meetingEndTime ? meetingEndTime.toLocaleString() : new Date(Date.now() + 3600000).toISOString();

  // Format metadata using helper function
  const metadata = formatAgentMetadata(userEmail, startTimeString, endTimeString, meetingContext.timeZone, organizer);
  // Build paging context from store
  const { pagingContext } = useAgentStore.getState();

  const pagingPayload = {
    action: options?.pagingAction ?? null,
    currentPage: options?.page ?? pagingContext?.currentPage ?? 0,
  };

  // Thêm context về platform và quy tắc paging để Agent trả về đúng định dạng
  const safeUserMsg = message && message.length > 800 ? `${message.slice(0, 800)}...` : message;
  const messageWithContext = `Message provided by user: ${safeUserMsg}, metadata: ${JSON.stringify(metadata)}, clientPaging: ${JSON.stringify(pagingPayload)}`;

  console.log("🚀 Payload send to Agent:", messageWithContext);

  // Gửi tin nhắn tới Agent trong cuộc trò chuyện hiện tại
  const replies = await client.askQuestionAsync(messageWithContext, conversationId || undefined);

  // Lấy phản hồi cuối cùng từ Agent (replies là array của Activity)
  const lastReply = replies?.[replies?.length - 1];
  console.log("🚀 Response from Agent:", replies);

  if (!lastReply) {
    return "Something went wrong! Please try again.";
  }
  return lastReply;
};

export const sendAgentsActivity = async (
  message: string,
  userEmail?: string,
  startTime?: Date | null,
  endTime?: Date | null,
  options?: {
    activityType?: string;
    name?: string;
    value?: unknown;
    channelData?: any;
    replyToId?: string;
    pagingAction?: "next" | "previous" | "goto";
    page?: number;
  }
): Promise<any> => {
  const client = await createClient();

  const meetingContext = getCurrentMeetingContext();

  const meetingStartTime = startTime || meetingContext.startTime;
  const meetingEndTime = endTime || meetingContext.endTime;
  const organizer = meetingContext.organizer || userEmail || "";

  if (!conversationId) {
    await startNewChat();
  }

  const startTimeString = meetingStartTime ? meetingStartTime.toLocaleString() : new Date().toISOString();
  const endTimeString = meetingEndTime ? meetingEndTime.toLocaleString() : new Date(Date.now() + 3600000).toISOString();

  const metadata = formatAgentMetadata(userEmail, startTimeString, endTimeString, meetingContext.timeZone, organizer);

  const { pagingContext } = useAgentStore.getState();
  const pagingPayload = {
    action: options?.pagingAction ?? null,
    currentPage: options?.page ?? pagingContext?.currentPage ?? 0,
  };

  const safeUserMsg = message && message.length > 800 ? `${message.slice(0, 800)}...` : message;
  const importantRules =
    '1. When user asks about resources, ALWAYS respond in valid JSON with this shape: {"type": "resource_list", "data": { "reply": "string" //Agent reply to user generate by AI, have info count item of list resources and total count in response API and allays show start and end time of meeting., "resources": [ { "capacity": number, "id": number, "email": "string", "isAvailable": boolean, "locationId": number, "locationPath": "string", "name": "string", "type": "string", "categories": ["string"] } ], "infoPagination":"string" //Response text generate by AI show text about paging have infomation about current page number and total page number, suggest user load more or load previous, "hasNext": boolean, "hasPrevious": boolean, "currentPage": number, "totalPage": number } } 2. Pagination behavior ONLY applies when type == "resource_list". When clientPaging.action is provided ("next", "previous", or "goto" with page), you MUST use it along with any available tokens to return the correct next/previous page. 3. If do not have resource, you only response text generate by AI, do not response anything else.';

  const messageWithContext = `Message provided by user: ${safeUserMsg}, metadata: ${JSON.stringify(
    metadata
  )}, clientPaging: ${JSON.stringify(pagingPayload)}, Important Rules: ${importantRules}`;

  const activity: any = {
    type: options?.activityType || "message",
    text: messageWithContext,
    ...(options?.name ? { name: options.name } : {}),
    ...(typeof options?.value !== "undefined" ? { value: options.value } : {}),
    ...(options?.replyToId ? { replyToId: options.replyToId } : {}),
    channelData: {
      ...(options?.channelData || {}),
      metadata,
      clientPaging: pagingPayload,
    },
  };

  const replies = await client.sendActivity(activity as any, conversationId || undefined);
  console.log("🚀 ~ sendAgentsActivity ~ replies:", replies);
  const lastReply = replies?.[replies?.length - 1];
  if (!lastReply) {
    return "Something went wrong! Please try again.";
  }
  return lastReply;
};